<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
    </div>

    <div class="page-container">
    <!-- Tab 切換列 -->
    <div class="tab-bar">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'bindings' }"
        @click="switchTab('bindings')"
      >
        <i class="ri-user-follow-line"></i>
        好友列表
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'block' }"
        @click="switchTab('block')"
      >
        <i class="ri-forbid-line"></i>
        推播失敗紀錄
      </button>
    </div>

    <!-- ===================== Tab: 好友綁定紀錄 ===================== -->
    <div v-show="activeTab === 'bindings'">
      <div class="line-bindings-intro" :class="{ 'is-expanded': introOpen }">
        <button type="button" class="intro-toggle" @click="introOpen = !introOpen">
          <span class="intro-title">
            <i class="ri-booklet-line"></i>
            舊好友補齊說明
          </span>
          <span class="intro-toggle-action">
            <span class="intro-toggle-label">{{ introOpen ? '收起' : '查看說明' }}</span>
            <i :class="introOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
          </span>
        </button>
        <div v-show="introOpen" class="intro-body">
          <p>
            本頁明細預設只顯示<strong>接入本系統後</strong>透過 Webhook、LIFF、QR Code 等互動建立的好友紀錄。
            若官方帳號在接入前已有好友，請使用下方工具補齊。
          </p>
          <ul>
            <li><strong>從 LINE 同步</strong>：背景執行，向 LINE 官方 API 分批取得好友 User ID 並寫入本系統（需認證或優質帳號；不含 PC 版 LINE 好友）。可關閉頁面，稍後回來查看進度。</li>
            <li><strong>匯入 CSV</strong>：若無法使用同步 API，可上傳含 <code>line_user_id</code> 的 CSV；選填 <code>member_code</code> 可一併綁定會員。</li>
            <li>同步／匯入後，顯示名稱需等好友互動或於詳情頁「刷新 Profile」才會更新。</li>
          </ul>
        </div>
      </div>

      <div class="page-toolbar">
        <select
          v-model="selectedLineConfigId"
          class="form-control filter-select"
          @change="onLineConfigChange"
        >
          <option
            v-for="config in lineConfigs"
            :key="config.id"
            :value="config.id"
          >
            {{ config.configName || config.name || config.id }}
          </option>
        </select>
        <div class="search-box">
          <input
            v-model="searchKeyword"
            type="text"
            class="form-control"
            placeholder="搜尋會員編號、LINE ID..."
            @input="handleSearch"
          />
          <i class="ri-search-line"></i>
        </div>
        <button class="btn btn-secondary" @click="loadBindings">
          <i class="ri-refresh-line"></i> 重新整理
        </button>
        <button
          class="btn btn-primary"
          :disabled="!selectedLineConfigId || isSyncActive"
          @click="handleSyncFromLine"
        >
          <i class="ri-cloud-download-line"></i>
          {{ isSyncActive ? '同步中...' : '從 LINE 同步' }}
        </button>
        <button
          class="btn btn-secondary"
          :disabled="!selectedLineConfigId"
          @click="openImportModal"
        >
          <i class="ri-upload-2-line"></i> 匯入 CSV
        </button>
      </div>

      <div v-if="syncJob" class="sync-job-panel" :class="`sync-job-panel--${syncJob.status}`">
        <div class="sync-job-head">
          <strong>LINE 好友同步</strong>
          <span class="sync-job-status">{{ syncJobStatusLabel(syncJob.status) }}</span>
        </div>
        <div v-if="isSyncActive" class="sync-job-progress">
          <div class="sync-progress-bar">
            <div class="sync-progress-fill" :style="{ width: `${syncJob.progress || 0}%` }"></div>
          </div>
          <span class="sync-progress-text">{{ syncJob.progress || 0 }}%</span>
        </div>
        <div class="sync-job-stats">
          <span>LINE 好友：{{ syncJob.totalFromLine ?? 0 }}</span>
          <span>批次：{{ syncJob.completedChunks ?? 0 }} / {{ syncJob.totalChunks ?? 0 }}</span>
          <span>新增：{{ syncJob.created ?? 0 }}</span>
          <span>重新啟用：{{ syncJob.reactivated ?? 0 }}</span>
          <span>已存在：{{ syncJob.alreadyTracked ?? 0 }}</span>
        </div>
        <p v-if="syncJob.errorMessage" class="sync-job-error">{{ syncJob.errorMessage }}</p>
        <p v-if="syncJob.errorCode === 'LINE_OA_NOT_VERIFIED'" class="sync-job-hint">
          此限制來自 LINE 官方 API，認證完成前無法使用「從 LINE 同步」。可先使用上方「匯入 CSV」，或待 OA 認證通過後再試。
        </p>
        <div v-if="syncJob.status === 'failed' && syncJob.errorCode !== 'LINE_OA_NOT_VERIFIED'" class="sync-job-actions">
          <button type="button" class="btn btn-sm btn-primary" @click="handleRetrySync">
            重試同步
          </button>
        </div>
      </div>

      <div class="stats-grid" v-if="statistics">
        <div class="stat-card">
          <div class="stat-icon primary">
            <i class="ri-group-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">
              有效好友數
              <span class="stat-source line-source" v-if="lineOfficialReady">LINE 官方（{{ formatStatDate(statistics.lineOfficial.date) }}）</span>
              <span class="stat-source db-source" v-else>本系統紀錄</span>
            </div>
            <div class="stat-value">
              <template v-if="lineOfficialReady">
                {{ statistics.lineOfficial.targetedReaches ?? '-' }}
              </template>
              <template v-else>
                {{ statistics.activeFriends ?? 0 }}
              </template>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon danger">
            <i class="ri-forbid-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">
              <template v-if="lineOfficialReady">封鎖數／封鎖率</template>
              <template v-else>推播不可達／比率</template>
              <span class="stat-source line-source" v-if="lineOfficialReady">LINE 官方（{{ formatStatDate(statistics.lineOfficial.date) }}）</span>
              <span class="stat-source db-source" v-else>推播失敗偵測</span>
            </div>
            <div class="stat-value">
              <template v-if="lineOfficialReady">
                {{ statistics.lineOfficial.blocks ?? '-' }}
                <span class="stat-value-rate">/ {{ getBindingsBlockRate() }}</span>
              </template>
              <template v-else>
                {{ statistics.blockedFriends ?? 0 }}
                <span class="stat-value-rate">/ {{ getBindingsBlockRate() }}</span>
              </template>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon success">
            <i class="ri-user-add-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">
              本週新增好友（7天內）
              <span class="stat-source db-source">本系統紀錄</span>
            </div>
            <div class="stat-value">{{ statistics.newFriendsThisWeek || 0 }}</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon info">
            <i class="ri-user-follow-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">
              本月新增好友（30天內）
              <span class="stat-source db-source">本系統紀錄</span>
            </div>
            <div class="stat-value">{{ statistics.newFriendsThisMonth || 0 }}</div>
          </div>
        </div>
      </div>

      <div
        class="line-bindings-intro data-notice-collapsible"
        :class="{ 'is-expanded': glossaryOpen }"
        v-if="statistics"
      >
        <button type="button" class="intro-toggle" @click="glossaryOpen = !glossaryOpen">
          <span class="intro-title">
            <i class="ri-information-line"></i>
            名詞說明
          </span>
          <span class="intro-toggle-action">
            <span class="intro-toggle-label">{{ glossaryOpen ? '收起' : '查看說明' }}</span>
            <i :class="glossaryOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"></i>
          </span>
        </button>
        <div v-show="glossaryOpen" class="intro-body">
          <ul>
            <li><strong>好友</strong>：使用者已加入官方帳號（LINE <code>follow</code> 事件；本系統 <code>is_following = true</code>）。</li>
            <li><strong>封鎖</strong>：使用者封鎖或解除好友（LINE <code>unfollow</code> 事件；本系統 <code>is_following = false</code>）。統計卡「封鎖數」在有 LINE 官方資料時，與後台 Insight 一致。</li>
            <li><strong>推播不可達</strong>：本系統對該使用者推播失敗時的偵測標記（<code>is_push_blocked</code>），<em>不等同</em> LINE 官方封鎖數；詳見「推播失敗紀錄」Tab。</li>
            <li v-if="lineOfficialReady">
              <strong>有效好友數／封鎖數</strong> 來自 LINE 官方（{{ formatStatDate(statistics.lineOfficial.date) }}），包含接入本系統前的好友，因此通常大於下方明細。
            </li>
            <li v-else>
              <strong>有效好友數</strong> 僅統計本系統紀錄中的好友；接入前的舊好友不在範圍內，可用「從 LINE 同步」或「匯入 CSV」補齊。
            </li>
            <li><strong>下方明細</strong> 為本系統紀錄；「好友狀態」對應 LINE 好友／封鎖，「推播狀態」為推播是否可送達。</li>
            <li><strong>本週／本月新增</strong> 以首次加入時間為準；同步／匯入的舊好友會計入同步當日，不代表實際加好友日期。</li>
          </ul>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="loadBindings">重試</button>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>官方帳號</th>
              <th>會員編號</th>
              <th>LINE User ID</th>
              <th>LINE 顯示名稱</th>
              <th>綁定來源</th>
              <th>好友狀態</th>
              <th>推播狀態</th>
              <th>加好友時間</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="bindings.length === 0">
              <td colspan="9" class="empty-state">尚無好友紀錄</td>
            </tr>
            <tr v-for="item in bindings" :key="item.id">
              <td>{{ item.lineConfigName || '-' }}</td>
              <td>{{ item.memberCode || (item.member && item.member.memberCode) || '-' }}</td>
              <td><code>{{ item.lineUserId || '-' }}</code></td>
              <td>{{ item.lineDisplayName || '-' }}</td>
              <td>{{ formatSource(item.bindingSource || item.sourceType) }}</td>
              <td>
                <span :class="getFriendStatusBadgeClass(item)">
                  {{ getFriendStatusText(item) }}
                </span>
              </td>
              <td>
                <span :class="getPushStatusBadgeClass(item)">
                  {{ getPushStatusText(item) }}
                </span>
              </td>
              <td>{{ formatDate(item.boundAt || item.followedAt) }}</td>
              <td>
                <div class="action-buttons">
                  <button
                    class="btn btn-sm btn-secondary"
                    @click="handleDetail(item.id)"
                    title="檢視詳情"
                  >
                    <i class="ri-eye-line"></i>
                  </button>
                  <button
                    class="btn btn-sm btn-danger"
                    @click="handleDelete(item)"
                    title="刪除此筆好友紀錄（僅資料庫，不影響 LINE 官方好友）"
                  >
                    <i class="ri-delete-bin-line"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ListPagination
        v-if="pagination.totalPages > 1"
        :page="pagination.page"
        :total-pages="pagination.totalPages"
        :total="pagination.total"
        @change="changePage"
      />
    </div>

    <!-- ===================== Tab: 推播失敗紀錄 ===================== -->
    <div v-show="activeTab === 'block'">
      <div class="page-toolbar">
        <select
          v-model="selectedLineConfigId"
          class="form-control filter-select"
          @change="onLineConfigChange"
        >
          <option
            v-for="config in lineConfigs"
            :key="config.id"
            :value="config.id"
          >
            {{ config.configName || config.name || config.id }}
          </option>
        </select>
        <div class="filter-group">
          <select v-model="pbFilterEventType" class="form-control" @change="pbHandleFilterChange">
            <option value="">全部事件</option>
            <option value="blocked">推播失敗</option>
            <option value="unblocked">推播恢復</option>
          </select>
        </div>
        <div class="search-box">
          <input
            v-model="pbSearchKeyword"
            type="text"
            class="form-control"
            placeholder="搜尋 LINE User ID..."
            @input="pbHandleSearch"
          />
          <i class="ri-search-line"></i>
        </div>
        <button class="btn btn-secondary" @click="loadBlockLogs">
          <i class="ri-refresh-line"></i> 重新整理
        </button>
      </div>

      <!-- 推播失敗統計 -->
      <div class="stats-grid" v-if="pbStatistics">
        <div class="stat-card">
          <div class="stat-icon danger">
            <i class="ri-forbid-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">
              <template v-if="pbLineOfficialReady">封鎖數</template>
              <template v-else>推播不可達</template>
              <span class="stat-source line-source" v-if="pbLineOfficialReady">LINE 官方（{{ formatStatDate(pbStatistics.lineOfficial.date) }}）</span>
              <span class="stat-source db-source" v-else>推播失敗偵測</span>
            </div>
            <div class="stat-value">
              <template v-if="pbLineOfficialReady">{{ pbStatistics.lineOfficial.blocks ?? '-' }}</template>
              <template v-else>{{ pbStatistics.uniqueUsers || 0 }}</template>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon info">
            <i class="ri-bar-chart-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">
              推播不可達率
              <span class="stat-source line-source" v-if="pbLineOfficialReady">LINE 官方封鎖率</span>
              <span class="stat-source db-source" v-else>本系統估算</span>
            </div>
            <div class="stat-value">
              <template v-if="pbLineOfficialReady">
                {{ pbStatistics.lineOfficial.followers > 0
                  ? ((pbStatistics.lineOfficial.blocks / pbStatistics.lineOfficial.followers) * 100).toFixed(1) + '%'
                  : '-' }}
              </template>
              <template v-else>{{ formatBlockRate(pbStatistics.blockRate) }}</template>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon warning">
            <i class="ri-calendar-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">最近 7 天推播失敗</div>
            <div class="stat-value">{{ pbStatistics.blockedLast7Days || 0 }}</div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-icon warning">
            <i class="ri-calendar-2-line"></i>
          </div>
          <div class="stat-content">
            <div class="stat-label">最近 30 天推播失敗</div>
            <div class="stat-value">{{ pbStatistics.blockedLast30Days || 0 }}</div>
          </div>
        </div>
      </div>

      <div v-if="pbLoading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="pbError" class="error-state">
        <p class="error-message">{{ pbError }}</p>
        <button class="btn btn-secondary" @click="loadBlockLogs">重試</button>
      </div>

      <div v-else class="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>LINE 用戶</th>
              <th>事件類型</th>
              <th>事件來源</th>
              <th>失敗原因</th>
              <th>偵測時間</th>
              <th>恢復時間</th>
              <th>推播狀態</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="blockLogs.length === 0">
              <td colspan="7" class="empty-state">尚無推播失敗紀錄</td>
            </tr>
            <tr v-for="item in blockLogs" :key="item.id">
              <td>
                <div class="line-user-cell">
                  <img
                    v-if="item.linePictureUrl"
                    :src="item.linePictureUrl"
                    class="line-avatar"
                    :alt="item.lineDisplayName"
                  />
                  <div class="line-avatar line-avatar-placeholder" v-else>
                    <i class="ri-user-3-line"></i>
                  </div>
                  <div class="line-user-info">
                    <span class="line-display-name">{{ item.lineDisplayName || '未知用戶' }}</span>
                    <code class="line-user-id">{{ item.lineUserId }}</code>
                  </div>
                </div>
              </td>
              <td>
                <span :class="getEventTypeBadgeClass(item.eventType)">
                  {{ formatEventType(item.eventType) }}
                </span>
              </td>
              <td>{{ formatEventSource(item.eventSource) }}</td>
              <td>{{ item.blockReason || '-' }}</td>
              <td>{{ formatDate(item.blockedAt) }}</td>
              <td>{{ formatDate(item.unblockedAt) }}</td>
              <td>
                <span :class="item.unblockedAt ? 'badge badge-success' : 'badge badge-warning'">
                  {{ item.unblockedAt ? '已恢復' : '不可達' }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ListPagination
        v-if="pbPagination.totalPages > 1"
        :page="pbPagination.page"
        :total-pages="pbPagination.totalPages"
        :total="pbPagination.totalItems"
        @change="pbChangePage"
      />
    </div>
    </div>

    <!-- 匯入 CSV Modal -->
    <div v-if="showImportModal" class="modal-overlay" @click.self="closeImportModal">
      <div class="modal-container">
        <div class="modal-header">
          <h3>匯入舊好友</h3>
          <button type="button" class="modal-close" @click="closeImportModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <p class="import-hint">
            上傳 CSV 檔案，第一列為欄位名稱。必填：<code>line_user_id</code>（U 開頭 33 字元）；選填：<code>member_code</code>（綁定既有會員）。
          </p>
          <div class="import-template-row">
            <span class="import-template-label">CSV 範本</span>
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="downloadImportTemplate">
              <i class="ri-download-line"></i>
              下載範本 CSV
            </button>
          </div>
          <div class="import-sample">
            <strong>範例預覽：</strong>
            <pre>line_user_id,member_code
U1234567890abcdef1234567890abcdef,
Uabcdef1234567890abcdef1234567890,M001</pre>
          </div>
          <div
            class="import-dropzone"
            :class="{ 'is-dragover': importDragging }"
            @dragover.prevent="importDragging = true"
            @dragleave.prevent="importDragging = false"
            @drop.prevent="handleImportDrop"
          >
            <input
              ref="importFileInput"
              type="file"
              accept=".csv,text/csv,text/plain"
              style="display: none"
              @change="handleImportFileSelect"
            />
            <template v-if="importFileName">
              <i class="ri-file-text-line"></i>
              <span>{{ importFileName }}（{{ importItems.length }} 筆）</span>
              <button type="button" class="btn btn-sm btn-secondary" @click="clearImportFile">重新選擇</button>
            </template>
            <template v-else>
              <i class="ri-upload-cloud-2-line"></i>
              <span>拖曳 CSV 到此，或</span>
              <button type="button" class="btn btn-sm btn-primary" @click="$refs.importFileInput.click()">
                選擇檔案
              </button>
            </template>
          </div>
          <p v-if="importParseError" class="import-error">{{ importParseError }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="closeImportModal">取消</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="importing || importItems.length === 0"
            @click="handleImportSubmit"
          >
            {{ importing ? '匯入中...' : `匯入 ${importItems.length || 0} 筆` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { usePageTitle } from '@/composables/usePageTitle.js';
import lineBindingApi from '@/api/modules/lineBinding.js';
import lineBlockApi from '@/api/modules/lineBlock.js';
import lineConfigApi from '@/api/modules/lineConfig.js';
import Swal from 'sweetalert2';

export default {
  name: 'LineBindingList',
  setup() {
    const { pageTitle } = usePageTitle('好友資料');
    return { pageTitle };
  },
  data() {
    return {
      activeTab: 'bindings',
      introOpen: false,
      glossaryOpen: false,

      // 共用
      selectedLineConfigId: '',
      lineConfigs: [],

      // 好友綁定
      loading: false,
      error: null,
      searchKeyword: '',
      bindings: [],
      statistics: null,
      syncJob: null,
      syncPollTimer: null,
      showImportModal: false,
      importDragging: false,
      importFileName: '',
      importItems: [],
      importParseError: null,
      importing: false,
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
        totalPages: 0
      },
      searchTimeout: null,

      // 封鎖紀錄
      pbLoading: false,
      pbError: null,
      pbSearchKeyword: '',
      pbFilterEventType: '',
      blockLogs: [],
      pbStatistics: null,
      pbPagination: {
        page: 1,
        pageSize: 20,
        totalItems: 0,
        totalPages: 0
      },
      pbSearchTimeout: null
    };
  },
  computed: {
    lineOfficialReady() {
      return this.statistics?.lineOfficial?.status === 'ready' &&
        this.statistics?.lineOfficial?.followers != null;
    },
    pbLineOfficialReady() {
      return this.pbStatistics?.lineOfficial?.status === 'ready' &&
        this.pbStatistics?.lineOfficial?.blocks != null;
    },
    isSyncActive() {
      return ['pending', 'fetching', 'processing'].includes(this.syncJob?.status);
    }
  },
  mounted() {
    // 從 query 判斷預設 Tab
    if (this.$route.query.tab === 'block') {
      this.activeTab = 'block';
    }
    this.loadLineConfigs();
  },
  beforeUnmount() {
    this.stopSyncPolling();
  },
  methods: {
    switchTab(tab) {
      this.activeTab = tab;
      this.$router.replace({ query: tab === 'block' ? { tab: 'block' } : {} });
      if (tab === 'block' && this.blockLogs.length === 0) {
        this.loadBlockLogs();
        this.loadBlockStatistics();
      }
    },

    // ── 共用 ──────────────────────────────────────────────
    async loadLineConfigs() {
      try {
        const response = await lineConfigApi.list({ pageSize: 100 });
        if (response && response.success && response.data) {
          const items = response.data.items || response.data || [];
          this.lineConfigs = Array.isArray(items) ? items : [];
          if (this.lineConfigs.length > 0 && !this.selectedLineConfigId) {
            this.selectedLineConfigId = this.lineConfigs[0].id;
          }
        }
      } catch (error) {
        console.error('載入 LINE 官方帳號列表失敗:', error);
      }
      this.loadBindings();
      this.loadStatistics();
      if (this.selectedLineConfigId) {
        this.loadActiveSyncJob();
      }
      if (this.activeTab === 'block') {
        this.loadBlockLogs();
        this.loadBlockStatistics();
      }
    },

    onLineConfigChange() {
      this.pagination.page = 1;
      this.pbPagination.page = 1;
      this.stopSyncPolling();
      this.syncJob = null;
      this.loadBindings();
      this.loadStatistics();
      this.loadActiveSyncJob();
      if (this.activeTab === 'block') {
        this.loadBlockLogs();
        this.loadBlockStatistics();
      }
    },

    // ── 好友綁定 ──────────────────────────────────────────
    async loadBindings() {
      this.loading = true;
      this.error = null;
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          search: this.searchKeyword
        };
        if (this.selectedLineConfigId) {
          params.lineConfigId = this.selectedLineConfigId;
        }
        const response = await lineBindingApi.list(params);
        if (response && response.success) {
          this.bindings = response.data?.items || response.data || [];
          const pag = response.data?.pagination || response.pagination;
          if (pag) {
            this.pagination = {
              page: pag.page ?? this.pagination.page,
              pageSize: pag.pageSize ?? this.pagination.pageSize,
              total: pag.total ?? pag.totalItems ?? this.pagination.total,
              totalPages: pag.totalPages ?? this.pagination.totalPages
            };
          }
        }
      } catch (error) {
        console.error('載入綁定記錄失敗:', error);
        this.error = error?.response?.data?.message || error?.message || '載入失敗，請稍後再試';
      } finally {
        this.loading = false;
      }
    },

    async loadStatistics() {
      if (!this.selectedLineConfigId) return;
      try {
        const response = await lineBindingApi.getStatistics({ lineConfigId: this.selectedLineConfigId });
        if (response && response.success && response.data) {
          this.statistics = response.data;
        }
      } catch (error) {
        console.error('載入統計資訊失敗:', error);
      }
    },

    changePage(page) {
      this.pagination.page = page;
      this.loadBindings();
    },

    handleSearch() {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1;
        this.loadBindings();
      }, 500);
    },

    handleDetail(id) {
      this.$router.push({ name: 'ClientLineBindingDetail', params: { id } });
    },

    async handleSyncFromLine() {
      if (!this.selectedLineConfigId || this.isSyncActive) return;

      const configName = this.lineConfigs.find(c => c.id === this.selectedLineConfigId)?.configName
        || '目前官方帳號';

      const r = await Swal.fire({
        title: '從 LINE 同步好友？',
        html:
          `<p>將在背景向 LINE 官方 API 分批取得 <strong>${this.escapeHtml(configName)}</strong> 的好友 User ID 並寫入本系統。</p>` +
          '<ul style="text-align:left;margin:12px 0 0;font-size:14px;color:#475569;line-height:1.6;padding-left:20px">' +
          '<li>需為 LINE 認證或優質帳號</li>' +
          '<li>不含 PC 版 LINE 好友</li>' +
          '<li>不會自動綁定會員或更新顯示名稱</li>' +
          '<li>同步在背景執行，可關閉此頁稍後回來查看進度</li>' +
          '</ul>',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: '開始同步',
        cancelButtonText: '取消',
        focusCancel: true
      });
      if (!r.isConfirmed) return;

      try {
        const response = await lineBindingApi.syncFromLine(this.selectedLineConfigId);
        if (response?.success && response.data?.jobId) {
          this.syncJob = response.data;
          this.startSyncPolling(response.data.jobId);
          this.$message?.success?.('同步任務已建立，正在背景執行');
        } else {
          this.$message?.error(response?.message || '建立同步任務失敗');
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || '建立同步任務失敗，請稍後再試';
        await Swal.fire({ title: '同步失敗', text: msg, icon: 'error' });
      }
    },

    syncJobStatusLabel(status) {
      const map = {
        pending: '等待中',
        fetching: '拉取 LINE 好友',
        processing: '寫入中',
        completed: '已完成',
        failed: '失敗',
        cancelled: '已取消'
      };
      return map[status] || status;
    },

    async loadActiveSyncJob() {
      if (!this.selectedLineConfigId) {
        this.syncJob = null;
        return;
      }
      try {
        const response = await lineBindingApi.getActiveSyncJob(this.selectedLineConfigId);
        if (response?.success && response.data?.jobId) {
          this.syncJob = response.data;
          this.startSyncPolling(response.data.jobId);
        } else {
          this.syncJob = null;
        }
      } catch (error) {
        console.error('查詢進行中同步任務失敗:', error);
      }
    },

    startSyncPolling(jobId) {
      this.stopSyncPolling();
      this.pollSyncJob(jobId);
      this.syncPollTimer = setInterval(() => {
        this.pollSyncJob(jobId);
      }, 2000);
    },

    stopSyncPolling() {
      if (this.syncPollTimer) {
        clearInterval(this.syncPollTimer);
        this.syncPollTimer = null;
      }
    },

    async pollSyncJob(jobId) {
      try {
        const response = await lineBindingApi.getSyncJob(jobId);
        if (!response?.success || !response.data) return;

        this.syncJob = response.data;
        const status = response.data.status;

        if (status === 'completed') {
          this.stopSyncPolling();
          this.pagination.page = 1;
          await this.loadBindings();
          await this.loadStatistics();
          await Swal.fire({
            title: '同步完成',
            html:
              `<p>LINE 好友總數：<strong>${response.data.totalFromLine ?? 0}</strong></p>` +
              `<p>新增：<strong>${response.data.created ?? 0}</strong>　` +
              `重新啟用：<strong>${response.data.reactivated ?? 0}</strong>　` +
              `已存在：<strong>${response.data.alreadyTracked ?? 0}</strong></p>`,
            icon: 'success'
          });
        } else if (status === 'failed' || status === 'cancelled') {
          this.stopSyncPolling();
        }
      } catch (error) {
        console.error('輪詢同步任務失敗:', error);
      }
    },

    async handleRetrySync() {
      if (!this.syncJob?.jobId) return;
      try {
        const response = await lineBindingApi.retrySyncJob(this.syncJob.jobId);
        if (response?.success && response.data?.jobId) {
          this.syncJob = response.data;
          this.startSyncPolling(response.data.jobId);
          this.$message?.success?.('已重新建立同步任務');
        } else {
          this.$message?.error(response?.message || '重試失敗');
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || '重試失敗';
        await Swal.fire({ title: '重試失敗', text: msg, icon: 'error' });
      }
    },

    openImportModal() {
      this.showImportModal = true;
      this.importFileName = '';
      this.importItems = [];
      this.importParseError = null;
    },

    closeImportModal() {
      if (this.importing) return;
      this.showImportModal = false;
      this.importDragging = false;
      this.importFileName = '';
      this.importItems = [];
      this.importParseError = null;
    },

    downloadImportTemplate() {
      const sample = [
        'line_user_id,member_code',
        'U1234567890abcdef1234567890abcdef,',
        'Uabcdef1234567890abcdef1234567890,M001'
      ].join('\n');
      const blob = new Blob(['\uFEFF' + sample], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'LINE舊好友匯入範本.csv';
      a.click();
      URL.revokeObjectURL(url);
    },

    clearImportFile() {
      this.importFileName = '';
      this.importItems = [];
      this.importParseError = null;
      if (this.$refs.importFileInput) {
        this.$refs.importFileInput.value = '';
      }
    },

    handleImportFileSelect(event) {
      const file = event.target.files?.[0];
      if (file) this.parseImportCsvFile(file);
    },

    handleImportDrop(event) {
      this.importDragging = false;
      const file = event.dataTransfer.files?.[0];
      if (file) this.parseImportCsvFile(file);
    },

    parseImportCsvFile(file) {
      this.importParseError = null;
      const reader = new FileReader();
      reader.onload = () => {
        try {
          const text = String(reader.result || '').replace(/^\uFEFF/, '');
          const items = this.parseImportCsvText(text);
          if (items.length === 0) {
            this.importParseError = 'CSV 無有效資料列';
            this.importItems = [];
            this.importFileName = '';
            return;
          }
          if (items.length > 5000) {
            this.importParseError = '單次最多匯入 5000 筆';
            this.importItems = [];
            this.importFileName = '';
            return;
          }
          this.importItems = items;
          this.importFileName = file.name;
        } catch (e) {
          this.importParseError = e.message || 'CSV 解析失敗';
          this.importItems = [];
          this.importFileName = '';
        }
      };
      reader.onerror = () => {
        this.importParseError = '讀取檔案失敗';
      };
      reader.readAsText(file, 'UTF-8');
    },

    parseImportCsvText(text) {
      const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length < 2) {
        throw new Error('CSV 至少需要標題列與一筆資料');
      }

      const headers = this.splitCsvLine(lines[0]).map(h => h.trim().toLowerCase());
      const lineUserIdIdx = headers.findIndex(h => h === 'line_user_id' || h === 'lineuserid');
      if (lineUserIdIdx === -1) {
        throw new Error('找不到 line_user_id 欄位');
      }
      const memberCodeIdx = headers.findIndex(h => h === 'member_code' || h === 'membercode');

      const items = [];
      for (let i = 1; i < lines.length; i++) {
        const cols = this.splitCsvLine(lines[i]);
        const lineUserId = (cols[lineUserIdIdx] || '').trim();
        if (!lineUserId) continue;
        const item = { lineUserId };
        if (memberCodeIdx >= 0) {
          const memberCode = (cols[memberCodeIdx] || '').trim();
          if (memberCode) item.memberCode = memberCode;
        }
        items.push(item);
      }
      return items;
    },

    splitCsvLine(line) {
      const result = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if (ch === ',' && !inQuotes) {
          result.push(current);
          current = '';
        } else {
          current += ch;
        }
      }
      result.push(current);
      return result;
    },

    async handleImportSubmit() {
      if (!this.selectedLineConfigId || this.importItems.length === 0 || this.importing) return;

      this.importing = true;
      try {
        const response = await lineBindingApi.importFollowers(
          this.selectedLineConfigId,
          this.importItems
        );
        if (response?.success && response.data) {
          const d = response.data;
          let errorHtml = '';
          if (d.errorCount > 0 && d.errors?.length) {
            const rows = d.errors.slice(0, 5).map(e =>
              `第 ${e.row} 列：${this.escapeHtml(e.message)}`
            ).join('<br>');
            errorHtml = `<p style="margin-top:12px;font-size:13px;color:#856404">部分列失敗（${d.errorCount} 筆）：<br>${rows}${d.errorCount > 5 ? '<br>…' : ''}</p>`;
          }
          await Swal.fire({
            title: '匯入完成',
            html:
              `<p>共 ${d.totalRows} 筆：新增 <strong>${d.created}</strong>、更新 <strong>${d.updated}</strong>、綁定會員 <strong>${d.memberBound}</strong></p>` +
              errorHtml,
            icon: d.errorCount > 0 ? 'warning' : 'success'
          });
          this.closeImportModal();
          this.pagination.page = 1;
          await this.loadBindings();
          await this.loadStatistics();
        } else {
          this.$message?.error(response?.message || '匯入失敗');
        }
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || '匯入失敗，請稍後再試';
        this.importParseError = msg;
      } finally {
        this.importing = false;
      }
    },

    escapeHtml(text) {
      const s = String(text ?? '');
      return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    },

    async handleDelete(item) {
      const nameHint = item.lineDisplayName || item.lineUserId || item.id;
      const r = await Swal.fire({
        title: '確定刪除此筆好友綁定紀錄？',
        html:
          `<p>對象：<strong>${this.escapeHtml(nameHint)}</strong></p>` +
          '<p style="text-align:left;margin:12px 0 0;font-size:14px;color:#6c757d;line-height:1.5">' +
          '將一併移除本系統內關聯（掃碼工作階段指標、分享邀請指標、該官方帳號下的 LINE 好友標籤），' +
          '但不會刪除會員資料，也不會在 LINE 官方解除好友。' +
          '</p>',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '確定刪除',
        cancelButtonText: '取消',
        focusCancel: true,
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6c757d',
        reverseButtons: true
      });
      if (!r.isConfirmed) {
        return;
      }

      try {
        const response = await lineBindingApi.remove(item.id);
        if (response && response.success) {
          this.$message?.success('刪除成功');
          if (this.bindings.length === 1 && this.pagination.page > 1) {
            this.pagination.page -= 1;
          }
          await this.loadBindings();
          await this.loadStatistics();
        } else {
          this.$message?.error((response && response.message) || '刪除失敗');
        }
      } catch (err) {
        console.error('刪除好友紀錄失敗:', err);
        const errorMsg =
          err?.response?.data?.message || err?.message || '刪除失敗，請稍後再試';
        this.$message?.error(errorMsg);
      }
    },

    // ── 封鎖紀錄 ──────────────────────────────────────────
    async loadBlockLogs() {
      this.pbLoading = true;
      this.pbError = null;
      try {
        const params = {
          page: this.pbPagination.page,
          pageSize: this.pbPagination.pageSize
        };
        if (this.pbFilterEventType) params.eventType = this.pbFilterEventType;
        if (this.pbSearchKeyword) params.lineUserId = this.pbSearchKeyword;

        const response = await lineBlockApi.list(params);
        if (response && response.success) {
          this.blockLogs = response.data?.items || response.data || [];
          if (response.data?.pagination) {
            this.pbPagination = { ...this.pbPagination, ...response.data.pagination };
          }
        }
      } catch (error) {
        console.error('載入封鎖記錄失敗:', error);
        this.pbError = error?.response?.data?.message || error?.message || '載入失敗，請稍後再試';
      } finally {
        this.pbLoading = false;
      }
    },

    async loadBlockStatistics() {
      try {
        const params = {};
        if (this.selectedLineConfigId) params.lineConfigId = this.selectedLineConfigId;
        const response = await lineBlockApi.getStatistics(params);
        if (response && response.success && response.data) {
          this.pbStatistics = response.data;
        }
      } catch (error) {
        console.error('載入封鎖統計失敗:', error);
      }
    },

    pbChangePage(page) {
      this.pbPagination.page = page;
      this.loadBlockLogs();
    },

    pbHandleSearch() {
      clearTimeout(this.pbSearchTimeout);
      this.pbSearchTimeout = setTimeout(() => {
        this.pbPagination.page = 1;
        this.loadBlockLogs();
      }, 500);
    },

    pbHandleFilterChange() {
      this.pbPagination.page = 1;
      this.loadBlockLogs();
    },

    // ── 格式化工具 ────────────────────────────────────────
    formatSource(source) {
      const sourceMap = {
        'qrcode': 'QR Code',
        'campaign': '活動',
        'manual': '手動綁定',
        'webhook': 'Webhook',
        'follow': 'LINE 加好友',
        'unknown': '未標記（舊資料）',
        'short_url': '短網址',
        'liff_binding': 'LIFF 綁定（既有會員）',
        'liff_registration': 'LIFF 註冊',
        'liff': 'LIFF',
        'line_sync': 'LINE 同步',
        'import': 'CSV 匯入'
      };
      return sourceMap[source] || source || '-';
    },

    formatDate(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getFriendStatusText(item) {
      if (item.isFollowing === false) return '封鎖（非好友）';
      if (item.isFollowing === true) return '好友';
      return '-';
    },

    getFriendStatusBadgeClass(item) {
      if (item.isFollowing === true) return 'badge badge-success';
      if (item.isFollowing === false) return 'badge badge-danger';
      return 'badge badge-secondary';
    },

    getPushStatusText(item) {
      if (item.isFollowing === false) return '—';
      if (item.isPushBlocked) return '不可達';
      if (item.isFollowing === true) return '正常';
      return '-';
    },

    getPushStatusBadgeClass(item) {
      if (item.isFollowing === false) return 'badge badge-secondary';
      if (item.isPushBlocked) return 'badge badge-warning';
      if (item.isFollowing === true) return 'badge badge-success';
      return 'badge badge-secondary';
    },

    /** 好友列表 Tab：封鎖數／推播不可達 對應比率 */
    getBindingsBlockRate() {
      if (!this.statistics) return '-';

      if (this.lineOfficialReady) {
        const blocks = this.statistics.lineOfficial?.blocks;
        const followers = this.statistics.lineOfficial?.followers;
        if (blocks == null || followers == null) return '-';
        const total = followers + blocks;
        if (total <= 0) return '-';
        return `${((blocks / total) * 100).toFixed(1)}%`;
      }

      const blocked = this.statistics.blockedFriends ?? 0;
      const active = this.statistics.activeFriends ?? 0;
      const total = active + blocked;
      if (total <= 0) return '0%';
      return `${((blocked / total) * 100).toFixed(1)}%`;
    },

    formatStatDate(dateStr) {
      if (!dateStr) return '';
      const s = String(dateStr);
      if (s.length === 8) {
        return `${s.slice(0, 4)}/${s.slice(4, 6)}/${s.slice(6, 8)}`;
      }
      return s.replace(/-/g, '/');
    },

    formatEventType(eventType) {
      const typeMap = { 'blocked': '推播失敗', 'unblocked': '推播恢復' };
      return typeMap[eventType] || eventType || '-';
    },

    formatEventSource(eventSource) {
      const sourceMap = {
        'push_failed': '推播失敗',
        'webhook': 'Webhook',
        'api': 'API',
        'manual': '手動'
      };
      return sourceMap[eventSource] || eventSource || '-';
    },

    formatBlockRate(rate) {
      if (rate === null || rate === undefined) return '0%';
      return `${rate.toFixed(1)}%`;
    },

    getEventTypeBadgeClass(eventType) {
      if (eventType === 'blocked') return 'badge badge-danger';
      if (eventType === 'unblocked') return 'badge badge-success';
      return 'badge badge-secondary';
    }
  }
};
</script>

<style scoped>
@import '~@/assets/scss/components/_table-common.scss';
@import '~@/assets/scss/components/_list-page-common.scss';

/* Tab 列 */
.tab-bar {
  display: flex;
  gap: 4px;
  border-bottom: 2px solid #e5e7eb;
  margin-bottom: 20px;
}

.tab-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  background: none;
  font-size: 14px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-radius: 6px 6px 0 0;
  transition: all .15s;
}

.tab-btn i { font-size: 16px; }
.tab-btn:hover { color: #374151; background: #f9fafb; }
.tab-btn.active { color: #3b82f6; border-bottom-color: #3b82f6; background: #eff6ff; }

.filter-select {
  width: 200px;
  flex-shrink: 0;
}

/* 統計卡 */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.stat-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.stat-icon {
  width: 48px;
  height: 48px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  color: #666;
  font-size: 24px;
  flex-shrink: 0;
}

.stat-icon.primary { background: #e3f2fd; color: #1976d2; }
.stat-icon.success { background: #d4edda; color: #28a745; }
.stat-icon.info    { background: #d1ecf1; color: #17a2b8; }
.stat-icon.danger  { background: #f8d7da; color: #dc3545; }
.stat-icon.warning { background: #fff3cd; color: #e6a817; }

.stat-content { flex: 1; min-width: 0; }

.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
  line-height: 1.5;
}

.stat-hint {
  font-size: 12px;
  color: #999;
  font-weight: normal;
}

.stat-source {
  display: inline-block;
  font-size: 10px;
  font-weight: 500;
  padding: 1px 5px;
  border-radius: 3px;
  margin-left: 4px;
  vertical-align: middle;
}

.stat-source.line-source { background: #06c755; color: #fff; }
.stat-source.db-source   { background: #e9ecef; color: #6c757d; }

.stat-value {
  font-size: 24px;
  font-weight: 600;
  color: #333;
}

.stat-value-rate {
  font-size: 18px;
  font-weight: 500;
  color: #666;
}

/* 名詞說明（可展開） */
.data-notice-collapsible {
  background: #f0f7ff;
  border: 1px solid #b8d9f8;
  border-left-color: #3a6ea8;
  color: #3a6ea8;
  margin-bottom: 20px;
}

.data-notice-collapsible .intro-title {
  color: #1a4f8a;
}

.data-notice-collapsible .intro-title i {
  color: #3a6ea8;
}

.data-notice-collapsible .intro-toggle-action {
  color: #1a4f8a;
}

.data-notice-collapsible .intro-body ul li strong {
  color: #1a4f8a;
}

.data-notice-collapsible .intro-body ul li {
  margin-bottom: 3px;
}

/* 表格 */
.table-wrapper table { min-width: 1100px; }

code {
  background: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: #e83e8c;
}

/* 封鎖紀錄用戶欄 */
.line-user-cell {
  display: flex;
  align-items: center;
  gap: 10px;
}

.line-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
}

.line-avatar-placeholder {
  background: #e8f5e9;
  color: #43a047;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.line-user-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.line-display-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-user-id {
  background: #f5f5f5;
  padding: 1px 5px;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #999;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.line-bindings-intro {
  background: #e8f4ff;
  border: 1px solid #b3d7ff;
  border-left: 4px solid #1890ff;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
  font-size: 13px;
  color: #1a3a52;
  line-height: 1.65;
  min-width: 0;
}

.line-bindings-intro.is-expanded {
  padding: 14px 16px;
}

.line-bindings-intro .intro-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 0;
  border: none;
  background: transparent;
  color: inherit;
  text-align: left;
  cursor: pointer;
}

.line-bindings-intro .intro-title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0d3d66;
  display: flex;
  align-items: center;
  gap: 8px;
}

.line-bindings-intro .intro-title i {
  font-size: 20px;
  color: #1890ff;
}

.line-bindings-intro .intro-toggle-action {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  font-size: 13px;
  color: #0d3d66;
}

.line-bindings-intro .intro-body {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(24, 144, 255, 0.2);
}

.line-bindings-intro .intro-body p {
  margin: 0 0 10px 0;
}

.line-bindings-intro .intro-body ul {
  margin: 0;
  padding-left: 18px;
}

.line-bindings-intro .intro-body li {
  margin-bottom: 6px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: #fff;
  border-radius: 8px;
  width: 90%;
  max-width: 560px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
}

.modal-close {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}

.modal-body {
  padding: 20px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 16px 20px;
  border-top: 1px solid #eee;
}

.import-hint {
  margin: 0 0 12px;
  font-size: 14px;
  color: #555;
  line-height: 1.6;
}

.import-template-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
  padding: 10px 12px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
}

.import-template-label {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.import-sample {
  margin-bottom: 16px;
  font-size: 13px;
  color: #666;
}

.import-sample pre {
  margin: 8px 0 0;
  padding: 10px 12px;
  background: #f8f9fa;
  border-radius: 6px;
  font-size: 12px;
  overflow-x: auto;
}

.import-dropzone {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 120px;
  padding: 20px;
  border: 2px dashed #ced4da;
  border-radius: 8px;
  text-align: center;
  color: #666;
  transition: border-color 0.15s, background 0.15s;
}

.import-dropzone.is-dragover {
  border-color: #3b82f6;
  background: #eff6ff;
}

.import-dropzone i {
  font-size: 28px;
  color: #3b82f6;
}

.import-error {
  margin: 12px 0 0;
  color: #dc3545;
  font-size: 13px;
}

.sync-job-panel {
  margin-bottom: 16px;
  padding: 14px 16px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background: #f8fbff;
}

.sync-job-panel--failed {
  border-color: #fecaca;
  background: #fef2f2;
}

.sync-job-panel--completed {
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.sync-job-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.sync-job-status {
  font-size: 13px;
  font-weight: 600;
  color: #1d4ed8;
}

.sync-job-panel--failed .sync-job-status {
  color: #b91c1c;
}

.sync-job-panel--completed .sync-job-status {
  color: #15803d;
}

.sync-job-progress {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.sync-progress-bar {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
}

.sync-progress-fill {
  height: 100%;
  background: #3b82f6;
  transition: width 0.3s ease;
}

.sync-progress-text {
  font-size: 13px;
  font-weight: 600;
  color: #475569;
  min-width: 36px;
}

.sync-job-stats {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 16px;
  font-size: 13px;
  color: #475569;
}

.sync-job-error {
  margin: 10px 0 0;
  font-size: 13px;
  color: #b91c1c;
}

.sync-job-hint {
  margin: 8px 0 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.sync-job-actions {
  margin-top: 10px;
}
</style>
